import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// DEV-12077: Only bundle English in the main chunk. Other locales load on demand
// to reduce the critical bundle size from ~274KB to ~180KB gzipped.
import en from './locales/en.json';

export const SUPPORTED_LOCALES = ['en', 'es', 'de', 'fr', 'zh'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

// DEV-11204: locale-prefixed landing URLs (/es/about, /de/pricing, …). When a
// URL carries a `/<locale>` prefix it must win over the cookie so the page
// renders in the requested language (the prerendered HTML is already in that
// locale). The stock i18next `path` detector can't tell a locale segment from
// an ordinary one (`/about` would be read as the language "about" and break
// the bare-path cookie fallback), so use a custom detector that only fires on a
// genuine supported-locale prefix and otherwise defers to cookie/localStorage.
const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: 'urlLocalePrefix',
  lookup() {
    if (typeof window === 'undefined') return undefined;
    const seg = window.location.pathname.split('/')[1];
    return (SUPPORTED_LOCALES as readonly string[]).includes(seg) ? seg : undefined;
  },
});

// DEV-12077: Lazy-load non-English locales. When the language changes to es/de/fr/zh,
// dynamically import the JSON and add it to i18next resources. This keeps 400KB+ of
// locale data out of the critical bundle.
//
// DEV-12658: this listener must be attached BEFORE init(). With no backend,
// init() detects the URL/cookie locale and emits `languageChanged`
// synchronously; a listener attached afterwards never saw it, so es/de/fr/zh
// JSON never loaded, `resolvedLanguage` stayed "en", and useCMS loaded the
// English CMS chunk too — the whole marketing site rendered in English on
// /es/*, /de/*, … (production included).
const loadedLocales = new Set<string>(['en']);

const localeLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  es: () => import('./locales/es.json'),
  de: () => import('./locales/de.json'),
  fr: () => import('./locales/fr.json'),
  zh: () => import('./locales/zh.json'),
};

export function loadLocale(lng: string): void {
  const base = lng.split('-')[0];
  if (loadedLocales.has(base)) return;
  const loader = localeLoaders[base];
  if (!loader) return;
  loadedLocales.add(base);
  loader()
    .then((module) => {
      i18n.addResourceBundle(base, 'translation', module.default, true, true);
      // Re-resolve now that the bundle exists: `resolvedLanguage` only moves
      // off the English fallback on a language change, and that event is what
      // re-renders react-i18next consumers (and useCMS, which keys its
      // locale data off resolvedLanguage).
      if (i18n.language.split('-')[0] === base) void i18n.changeLanguage(i18n.language);
    })
    .catch((err) => {
      loadedLocales.delete(base);
      console.warn(`Failed to load locale ${base}:`, err);
    });
}

i18n.on('languageChanged', loadLocale);

// DEV-10449: locale resolution mirrors apps/app (DEV-10435) — URL prefix first
// (DEV-11204), then cookie, then localStorage, then English. Browser language
// is *not* used so that the public site behaves predictably for both casual and
// signed-in visitors.
// DEV-12077: non-EN locales loaded via lazy backend to keep them out of main bundle.
i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ['urlLocalePrefix', 'cookie', 'localStorage'],
      lookupCookie: 'NEXT_LOCALE',
      lookupLocalStorage: 'lc.publicLocale',
      caches: ['cookie', 'localStorage'],
    },
  });

// Belt and braces: if init resolved synchronously before any event reached the
// listener, load the detected locale now.
loadLocale(i18n.language);

// Mirror the active locale onto <html lang> so CSS can target it (e.g. the
// non-English nav breakpoint in global.css) and assistive tech picks up the
// right language. i18next does not do this on its own.
const syncHtmlLang = (lng: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng.split('-')[0] || 'en';
  }
};
syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
